import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Tile-style choice grid: single-select.
 * options: [{ value, label, hint? }]
 */
export function ChoiceGrid({ options, value, onChange, columns = 1, testid }) {
  const grid = columns === 2 ? "grid-cols-2" : "grid-cols-1";
  return (
    <div className={`grid ${grid} gap-2`}>
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={testid ? `${testid}-${opt.value}` : undefined}
            onClick={() => onChange(opt.value)}
            className={`text-left rounded-xl border-2 px-4 py-3 transition-all ${
              selected
                ? "border-primary bg-primary/10"
                : "border-border bg-muted/30 hover:border-primary/40"
            }`}
          >
            <p className="text-sm font-semibold">{opt.label}</p>
            {opt.hint && (
              <p className="text-xs text-muted-foreground mt-0.5">{opt.hint}</p>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Multi-select tile grid. value is an array of strings. */
export function MultiChoiceGrid({ options, value = [], onChange, columns = 2, testid }) {
  const grid = columns === 2 ? "grid-cols-2" : "grid-cols-1";
  const toggle = (v) =>
    value.includes(v) ? onChange(value.filter((x) => x !== v)) : onChange([...value, v]);
  return (
    <div className={`grid ${grid} gap-2`}>
      {options.map((opt) => {
        const selected = value.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={testid ? `${testid}-${opt.value}` : undefined}
            onClick={() => toggle(opt.value)}
            className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-left transition-all ${
              selected
                ? "border-primary bg-primary/10"
                : "border-border bg-muted/30 hover:border-primary/40"
            }`}
          >
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                selected ? "border-primary bg-primary" : "border-muted-foreground/40"
              }`}
            >
              {selected && <Check className="w-3 h-3 text-primary-foreground" />}
            </span>
            <span className="text-sm font-medium">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Continue / Skip footer. `valid` toggles the primary button. */
export function StepFooter({ onNext, valid, nextLabel = "Continue →", testid = "step-next" }) {
  return (
    <Button
      className="w-full h-11 text-base font-semibold mt-2"
      disabled={!valid}
      onClick={onNext}
      data-testid={testid}
    >
      {nextLabel}
    </Button>
  );
}

/** Heading block: title + optional subtitle. */
export function StepHeader({ title, subtitle }) {
  return (
    <div className="text-center mb-1 pt-4">
      <h2 className="text-2xl font-heading font-bold">{title}</h2>
      {subtitle && (
        <p className="text-muted-foreground text-sm mt-1.5">{subtitle}</p>
      )}
    </div>
  );
}

/** Toggle between two units (e.g., lbs / kg or ft+in / cm). */
export function UnitToggle({ left, right, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-full bg-muted/50 p-1" data-testid={testid}>
      {[left, right].map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-4 py-1 text-xs font-semibold rounded-full transition-all ${
            value === opt.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Slider with current-value badge. */
export function ValueSlider({ value, onChange, min, max, step = 1, suffix = "", testid }) {
  return (
    <div className="space-y-2">
      <div className="flex justify-center">
        <div className="rounded-xl bg-primary/10 px-4 py-1.5 text-lg font-bold text-primary">
          {value}
          {suffix}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full accent-primary"
      />
      <div className="flex justify-between text-[10px] text-muted-foreground px-1">
        <span>{min}{suffix}</span>
        <span>{max}{suffix}</span>
      </div>
    </div>
  );
}
